import useNavigationStore from '@src/stores/navigationStore';
import useRegistrationStore from '@src/stores/registrationStore';

import styles from './Informations.module.scss';

import BlockTitle from '@src/components/_shared/UI/BlockTitle';
import BackButton from '@src/components/_shared/BackButton';
import Container from '@src/components/_shared/Container';
import Subtitle from '@src/components/_shared/UI/Subtitle';
import { Form, Formik } from 'formik';
import ValidateButton from '@src/components/_shared/UI/ValidateButton';
import Input from '@src/components/_shared/UI/Input';
import { useMutation, useQuery } from '@tanstack/react-query';
import { companyUpdate } from '@src/api/Registration';
import ValidateChoiceButton from '@src/components/_shared/UI/ValidateChoiceButton';
import { useState } from 'react';
import { getReferencialData } from '@src/api/Referencial';
import Loader from '@src/components/_shared/UI/Loader';
import CompanySearch from '@src/components/_shared/UI/CompanySearch';
import { handleApiError } from '@src/utilities/functions';

const FORM_STEPS = {
    OTHERSHARE: 0,
    ISAFFILIATED: 1,
    ISREGULATED: 2,
    INVESTMENTS: 3
};

export default function Informations() {
    const { setSection, setBlock } = useNavigationStore();
    const { setField, registration } = useRegistrationStore();
    const [currentStep, setCurrentStep] = useState(FORM_STEPS.OTHERSHARE);
    const [notFound, setNotFound] = useState<boolean>(false);

    const { data } = useQuery({
        queryKey: [`getExpectationsData`],
        queryFn: () => getReferencialData({type: 'investment/expectations'})
    })

    const { basic, illegal_behaviors_prevention, investment_expectation } = registration.company;
    const { mutate: MutateIllegalBehaviorsPrevention, isPending: isMutateIllegalBehaviorsPreventionPending } = useMutation({
        mutationFn: () => companyUpdate({ value: { basic, illegal_behaviors_prevention }, is_psi: registration.partner_informations.is_partner, person_id: registration.person_id }),
        onSuccess: () => {
            goToNextStep()
        },
        onError: (error: any) => handleApiError(error)
    });

    const { mutate: MutateInvestmentExpectation, isPending: isMutateInvestmentExpectationPending } = useMutation({
        mutationFn: () => companyUpdate({ value: { basic, investment_expectation }, is_psi: registration.partner_informations.is_partner, person_id: registration.person_id }),
        onSuccess: () => {
            setSection(5)
            setBlock('AccountInformations')
        },
        onError: (error: any) => handleApiError(error)
    });

    const _onGoBack = () => {
        setSection(4)
        setBlock('LatestResults');
    };

    const goToNextStep = () => {
        setCurrentStep((prevStep) => prevStep + 1);
    };

    const goToPreviousStep = () => {
        setCurrentStep((prevStep) => (prevStep > 0 ? prevStep - 1 : prevStep));
    };

    const handleSelectHasOtherShareAccount = (value: boolean) => {
        setField('company.illegal_behaviors_prevention.is_listed_company', value ? null : false);
        setField('company.illegal_behaviors_prevention.has_other_share_account', value);
    }

    const handleIsListedCompany = (value: boolean) => {
        setField('company.illegal_behaviors_prevention.is_listed_company', value)
    }

    const handleIsAffiliatedWithListedCompany = (value: boolean) => {
        setField('company.illegal_behaviors_prevention.is_affiliated_with_listed_company', value)
        if (value === false) {
            goToNextStep()
        }
    }

    const handleSelectIsRegulatedEntity = (value: boolean) => {
        setField('company.illegal_behaviors_prevention.is_regulated_entity', value)
        MutateIllegalBehaviorsPrevention()
    }

    const handleSelectInvestmentChoice = (value: string) => {
        setField('company.investment_expectation', value)
        MutateInvestmentExpectation()
    }

    return (
        <Container>
            {currentStep === FORM_STEPS.OTHERSHARE && (
                <>
                    <BackButton onClick={_onGoBack}/>
                    <BlockTitle title="Information relative à la prévention des abus de marché et des délits d’initiés" />
                    <Subtitle content="La société détient-elle d’autres comptes titres ?" />
                    <div className={styles.buttons}>
                        <ValidateChoiceButton title="Oui" selected={registration.company.illegal_behaviors_prevention.has_other_share_account === true} onValidate={() => handleSelectHasOtherShareAccount(true)} />
                        <ValidateChoiceButton title="Non" selected={registration.company.illegal_behaviors_prevention.has_other_share_account === false} onValidate={() => handleSelectHasOtherShareAccount(false)} />
                    </div>
                    {registration.company.illegal_behaviors_prevention.has_other_share_account && (
                        <>
                            <Subtitle content="La société est-elle cotée ?" />
                            <div className={styles.buttons}>
                                <ValidateChoiceButton title="Oui" selected={registration.company.illegal_behaviors_prevention.is_listed_company === true} onValidate={() => handleIsListedCompany(true)} />
                                <ValidateChoiceButton title="Non" selected={registration.company.illegal_behaviors_prevention.is_listed_company === false} onValidate={() => handleIsListedCompany(false)} />
                            </div>
                        </>
                    )}
                    {registration.company.illegal_behaviors_prevention.has_other_share_account === false || (registration.company.illegal_behaviors_prevention.has_other_share_account === true && registration.company.illegal_behaviors_prevention.is_listed_company !== null) ? (
                        <ValidateButton title="Continuer" onClick={goToNextStep} />
                    ) : null}
                </>
            )}
            {currentStep === FORM_STEPS.ISAFFILIATED && (
                <>
                    <BackButton onClick={goToPreviousStep}/>
                    <BlockTitle title="La société est-elle filiale d’une société cotée ou maison mère d’une société cotée ?" />
                    <Subtitle content="Veuillez choisir une réponse" />
                    <div className={styles.buttons}>
                        <ValidateChoiceButton title="Oui" selected={registration.company.illegal_behaviors_prevention.is_affiliated_with_listed_company === true} onValidate={() => handleIsAffiliatedWithListedCompany(true)} />
                        <ValidateChoiceButton title="Non" selected={registration.company.illegal_behaviors_prevention.is_affiliated_with_listed_company === false} onValidate={() => handleIsAffiliatedWithListedCompany(false)} />
                    </div>
                    {registration.company.illegal_behaviors_prevention.is_affiliated_with_listed_company && (
                        <Formik
                            initialValues={{
                                listedCompanyName: registration.company.illegal_behaviors_prevention.listed_company_name,
                            }}
                            onSubmit={(values) => {
                                setField('company.illegal_behaviors_prevention.listed_company_name', values.listedCompanyName)
                                goToNextStep();
                                setNotFound(false);
                            }}
                        >
                            {({ handleChange, handleSubmit, values, touched, handleBlur, setFieldValue }) => {
                                return (
                                    <Form id="listedCompanyNameForm" autoComplete="off" className="registerForm">
                                        {notFound ?
                                            <Input
                                                title="Indiquez le nom de la société"
                                                name="listedCompanyName"
                                                id="listedCompanyName"
                                                width="100%"
                                                type="text"
                                                placeholder="Ex : L'Oréal"
                                                value={values.listedCompanyName}
                                                onChange={handleChange('listedCompanyName')}
                                                clearInput={() =>handleChange('listedCompanyName')('')}
                                                hasError={(touched.listedCompanyName && !values.listedCompanyName) as boolean}
                                                onBlur={handleBlur('listedCompanyName')}
                                            />
                                            :
                                            <CompanySearch
                                                setNotFound={setNotFound}
                                                selectedCompanyName={registration.company.illegal_behaviors_prevention.listed_company_name || ''}
                                                setCompany={(value: { name: string, isin: string }) => setFieldValue('listedCompanyName', value.name)}
                                            />
                                        }
                                        <ValidateButton title="Continuer" disabled={!values.listedCompanyName} onClick={handleSubmit} />
                                    </Form>
                                )
                            }}
                        </Formik>
                    )}
                </>
            )}

            {currentStep === FORM_STEPS.ISREGULATED && (
                <>
                    <BackButton onClick={goToPreviousStep}/>
                    <BlockTitle title="La société est-elle une entité régulée dans le secteur financier" />
                    {isMutateIllegalBehaviorsPreventionPending ?
                        <Loader type="SPINNER"/>
                        :
                        <>
                            <Subtitle content="Veuillez choisir une réponse" />
                            <div className={styles.buttons}>
                                <ValidateChoiceButton title="Oui" selected={registration.company.illegal_behaviors_prevention.is_regulated_entity === true} onValidate={() => handleSelectIsRegulatedEntity(true)} />
                                <ValidateChoiceButton title="Non" selected={registration.company.illegal_behaviors_prevention.is_regulated_entity === false} onValidate={() => handleSelectIsRegulatedEntity(false)} />
                            </div>
                        </>
                    }
                </>
            )}
            {currentStep === FORM_STEPS.INVESTMENTS && (
                <>
                    <BackButton onClick={goToPreviousStep}/>
                    <BlockTitle title="Placements" />
                    {isMutateInvestmentExpectationPending ?
                        <Loader type="SPINNER"/>
                        :
                        <>
                            <Subtitle content="Quelles sont vos attentes en terme d’investissement, sachant que plus le potentiel de performance d’un placement est élevé, plus son niveau de risque de perte du capital investi est important ?" />
                            <div className={styles.buttonsList}>
                                {Object.entries(data).map(([key, value]) => (
                                    <ValidateChoiceButton
                                        key={key}
                                        title={value as string}
                                        fullWidth
                                        selected={false}
                                        onValidate={() => handleSelectInvestmentChoice(key)}
                                    />
                                ))}
                            </div>
                        </>
                    }
                </>
            )}
        </Container>
    );
}
